import {
    BUILD_TIMESTAMP_UTC_MS,
    LAST_MERGED_PR_NUMBER,
} from './build-info';

/**
 * Copy for the release/build-info footer rendered below "Log out" in the
 * account (profile) dropdown (AB#9776, UI-273) — lets anyone looking at a
 * deployed environment tell which release they are on.
 *
 * Kept OUT of profile-menu.ts: originally to avoid conflicting with PR 6165,
 * which was open against that exact file (it has since merged). The split is
 * retained because the copy belongs with the other shared constants.
 *
 * Both values read as "unknown" unless the build swapped in the generated
 * build-info.prod.ts — that is `ng serve` / `ng test` by design, and was
 * ALSO every deployed environment until AB#9804 added the `deployed` build
 * configuration. See scripts/generate-build-info.mjs.
 */
export const RELEASE_INFO_COPY = {
    /** Adjacent timezone label — the timestamp format itself carries none. */
    timezoneLabel: 'UTC',
    /** Tooltip/title text disambiguating the timezone. */
    timestampTitle: 'Release build timestamp (UTC)',
    /** Rendered when no build timestamp was generated (ng serve / ng test). */
    timestampUnknown: 'Release timestamp unknown',
    /** Rendered when no "Merged PR" commit is found on this build's branch. */
    prUnknown: 'PR unknown',
} as const;

const pad = (value: number, length: number): string =>
    String(value).padStart(length, '0');

/**
 * Formats a UTC instant as `yyyy/mm/dd hh:mm:ss:milliseconds` — the exact
 * literal format requested (a colon, not a dot, before the milliseconds).
 * Carries no timezone field by design; see {@link RELEASE_INFO_COPY} for
 * the adjacent-label/title disambiguation instead of embedding one here.
 */
export function formatReleaseTimestampUtc(date: Date): string {
    const year = date.getUTCFullYear();
    const month = pad(date.getUTCMonth() + 1, 2);
    const day = pad(date.getUTCDate(), 2);
    const hours = pad(date.getUTCHours(), 2);
    const minutes = pad(date.getUTCMinutes(), 2);
    const seconds = pad(date.getUTCSeconds(), 2);
    const milliseconds = pad(date.getUTCMilliseconds(), 3);

    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}:${milliseconds}`;
}

/** Rendered release-timestamp text, or the unknown fallback. */
export function releaseTimestampDisplay(): string {
    return BUILD_TIMESTAMP_UTC_MS === null
        ? RELEASE_INFO_COPY.timestampUnknown
        : formatReleaseTimestampUtc(new Date(BUILD_TIMESTAMP_UTC_MS));
}

/** Rendered "PR NNNN" text, or the unknown fallback. */
export function lastMergedPrDisplay(): string {
    return LAST_MERGED_PR_NUMBER === null
        ? RELEASE_INFO_COPY.prUnknown
        : `PR ${LAST_MERGED_PR_NUMBER}`;
}
